import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const IMGS = {
  caretaker:
    "https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?fm=jpg&q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  nurse:
    "https://images.unsplash.com/photo-1581056771107-24ca5f033842?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxob21lJTIwbnVyc2luZyUyMGNhcmUlMjBkb2N0b3IlMjBlbGRlcmx5JTIwcGF0aWVudHxlbnwwfHx8fDE3ODg1Mzg2NDh8MA&ixlib=rb-4.1.0&q=85",
  baby:
    "https://images.unsplash.com/photo-1533483595632-c5f0e57a1936?fm=jpg&q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  japa:
    "https://images.unsplash.com/photo-1551934262-db2d7dd517f4?fm=jpg&q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  elder:
    "https://images.unsplash.com/photo-1773227055624-07b515ba87c5?fm=jpg&q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  icu:
    "https://images.unsplash.com/photo-1513224502586-d1e602410265?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwaWN1JTIwdmVudGlsYXRvciUyMGhlYWx0aGNhcmUlMjBlcXVpcG1lbnQlMjBob21lfGVufDB8fHx8MTc4ODUzODY0OHww&ixlib=rb-4.1.0&q=85",
};

const CARDS = [
  {
    key: "caretaker",
    title: "Caretaker / Attendant",
    desc: "Trained attendants for daily living, hygiene, feeding, mobility support and warm companionship.",
    span: "md:col-span-7",
    img: IMGS.caretaker,
    service: "Caretaker / Attendant",
  },
  {
    key: "nurse",
    title: "Nursing at Home",
    desc: "Only GNM & B.Sc qualified nurses. Injections, IV infusions, wound care, catheter and vitals monitoring.",
    span: "md:col-span-5",
    img: IMGS.nurse,
    service: "Nurse (GNM / B.Sc)",
  },
  {
    key: "baby",
    title: "Baby Caretaker",
    desc: "Gentle newborn care — feeding support, hygiene, sleep routines and parent guidance.",
    span: "md:col-span-4",
    img: IMGS.baby,
    service: "Baby Caretaker",
  },
  {
    key: "japa",
    title: "Japa Maid",
    desc: "Traditional postpartum care for mother and newborn — massage, nutrition and recovery support.",
    span: "md:col-span-4",
    img: IMGS.japa,
    service: "Japa Maid",
  },
  {
    key: "elder",
    title: "Elder Care",
    desc: "Dignified senior care — companionship, medication reminders, mobility and fall prevention.",
    span: "md:col-span-4",
    img: IMGS.elder,
    service: "Elder Care",
  },
];

const Services = ({ onBook }) => (
  <section id="services" className="py-16 md:py-24" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <SectionHeading
        number="01 — Services"
        title="Every kind of care,"
        accent="under one roof."
        subtitle="From a helping hand for daily chores to ICU-grade critical care at home — verified professionals, deployed fast across Delhi NCR."
        id="services"
      />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
        {CARDS.map((card, i) => (
          <motion.article
            key={card.key}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className={`group ${card.span} bg-white rounded-3xl overflow-hidden border border-slate-200 hover:border-tealbrand/40 hover:-translate-y-1 hover:shadow-xl transition-all duration-300`}
            data-testid={`service-card-${card.key}`}
          >
            <div className="relative h-52 overflow-hidden">
              <img
                src={card.img}
                alt={card.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent" />
              <h3 className="absolute bottom-4 left-6 font-display text-xl sm:text-2xl font-semibold text-white">
                {card.title}
              </h3>
            </div>
            <div className="p-6 flex items-start justify-between gap-4">
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{card.desc}</p>
              <button
                onClick={() => onBook(card.service)}
                data-testid={`service-book-${card.key}`}
                aria-label={`Book ${card.title}`}
                className="shrink-0 h-11 w-11 rounded-full border-2 border-navy/10 flex items-center justify-center text-navy group-hover:bg-tealbrand group-hover:border-tealbrand group-hover:text-white transition-all duration-300"
              >
                <ArrowUpRight className="h-5 w-5" />
              </button>
            </div>
          </motion.article>
        ))}

        <motion.article
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="group md:col-span-12 bg-navy rounded-3xl overflow-hidden grid md:grid-cols-2"
          data-testid="service-card-icu"
        >
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-tealbrand-light mb-4">
              24/7 Critical Care
            </p>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4">
              ICU at Home
            </h3>
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-8 max-w-md">
              ICU-trained nursing with ventilator, tracheostomy and bi-pap management —
              hospital-grade critical care without the hospital stay.
            </p>
            <div>
              <button
                onClick={() => onBook("ICU at Home Setup")}
                data-testid="service-book-icu"
                className="inline-flex items-center gap-2 bg-tealbrand text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white hover:text-navy transition-colors duration-300"
              >
                Set up ICU at Home <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="relative h-64 md:h-auto">
            <img
              src={IMGS.icu}
              alt="ICU at home setup with vital signs monitor"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy/60 to-transparent" />
          </div>
        </motion.article>
      </div>
    </div>
  </section>
);

export default Services;
